<template>
  <div v-show="searchIndex?.length && searchKey?.length && searchHost?.length" class="search--container"
       :class="containerClasses" ref="search" @click.stop @open="hideWidget = false" @close="hideWidget = true"
       @reset="reset" role="search">
    <a v-if="minimizeWidget"
       class="search--handle visual_menu--glass_effect textual_menu--glass_effect material-icons"
       href="#" :title="(hideWidget) ? 'Open Search' : 'Close Search'"
       @click.prevent="handleSearchHandleClick"></a>
    <div class="viewport">
      <text-input @input.native="submitAutocomplete" @search.native="submitSearch" v-model:input="searchTerm"
                  @focus="focusHandler" label="Search Menu Items" type="search" ref="input" required
                  wide></text-input>
    </div>
    <ul ref="autocomplete" class="autocomplete_results--container"
        :class="{'autocomplete_results--container-hide': !showResults || !autocompleteResults.length}"
        :style="{'max-height': autocompleteMaxHeight}">
      <li v-for="(result, index) in autocompleteResults" :key="index"
          @click="handleResultClick(result)" class="autocomplete_results--content">
        <div class="autocomplete_results--menu_icon">
          <i class="material-icons" v-if="allMenuIconsByTitle[result.menu].length">{{
              allMenuIconsByTitle[result.menu]
            }}</i>
          <span v-html="getResultData(result, 'menu')"></span>
        </div>
        <div>
          <h4 class="autocomplete_results--menu_title" v-html="getResultTitle(result)"></h4>
          <h5 v-if="getResultData(result, 'mi_subtitle').length" v-html="getResultData(result, 'mi_subtitle')"></h5>
          <p v-if="getResultData(result, 'mi_description')" v-html="getResultData(result, 'mi_description')"></p>
          <div v-html="getResultData(result, 'tags')"></div>
        </div>
        <a class="item_button--container item_button--container-view material-icons"
           @click.prevent.stop="handleResultClick(result)"
           href="#" :title="'Click to view ' + result.mi_title"></a>
        <!--        <a v-if="isCartEnabled && $store.state.selectedMenuItemIds.length"
                   class="item_button&#45;&#45;container item_button&#45;&#45;container-select material-icons"
                   @click.prevent.stop="$store.commit('toggleMenuItemSelected', [result.mi_id, menu?.menu_id || 0])" href="#"
                   :class="{'item_button&#45;&#45;container-active': -1 !== $store.state.selectedMenuItemIds.indexOf(result.mi_id)}"
                   :title="(-1 !== $store.state.selectedMenuItemIds.indexOf(result.mi_id)) ? 'Remove favorite item' : 'Add favorite item'"></a>-->
      </li>
      <li v-if="!autocompleteResults.length"><i>No Results Found</i></li>
    </ul>

    <div class="search--overlay" :class="{'search--overlay-active': !hideWidget}" @click="hideWidget = true"></div>
  </div>
</template>

<script lang="ts">
/**
 * Search widget for menus.
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.4.0
 * @copyright  Copyright (C) 2021-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2021-12-30 (1.0.0) (jhoward)
 *    - Refactored from textual-menu.vue to be used with visual-menu.vue as well.
 *
 *  2022-01-11 (1.1.0) (jhoward)
 *    - Support fixed positioning.
 *
 *  2022-03-07 (1.1.1) (jhoward)
 *    - Do not show menu search results if search input is blank.
 *
 *  2022-05-11 (1.2.0) (jhoward)
 *    - Update minimized layout.
 *
 *  2022-05-24 (1.2.1) (jhoward)
 *    - <search-menu> now uses .visual_menu--glass_effect for stylization.
 *    - <search-menu> refactored handle click logic to handleSearchHandleClick() method.
 *
 *  2023-04-21 (1.3.0) (jhoward)
 *    - <search-menu> now uses TypeScript.
 *
 *  2023-08-03 (1.3.1) (jhoward)
 *    - Erase search term after submitting search or closing the search widget in <search-menu>.
 *
 *  2023-04-21 (1.3.2) (jhoward)
 *    - Add `open` and `close` event listeners on <search-menu> container element.
 *    - Coding style improvements in <search-menu>.
 *
 *  2023-08-26 (1.3.3) (jhoward)
 *    - Use store for selected items in <search-menu>.
 *
 *  2023-12-03 (1.3.4) (jhoward)
 *    - Layout improvements in <search-menu>.
 *    - Add isPublicDevice prop to <search-menu>.
 *    - Only clear search results and term if isPublicDevice prop is set to true in <search-menu>.
 *    - Add isCartEnabled prop to <search-menu>.
 *    - Do not show "favorite" icon in search results if isCartEnabled prop is set to false in <search-menu>.
 *
 *  2025-04-08 (1.4.0) (jhoward)
 *    - Refactor layout in <search-menu> to show more information in a way that is easier to read.
 *    - Remove favorite button in <search-menu>.
 *    - Add "view-menu-item" emits in <search-menu>.
 *    - Add "relatedMenus" prop in <search-menu>.
 *    - Search all menus available on the page in <search-menu>.
 *    - Add <search-menu>::allMenuIdsByTitle computed property.
 *    - Add <search-menu>::allMenuIconsByTitle computed property.
 *    - Add <search-menu>::getResultTitle() method.
 *    - Add <search-menu>::getResultData() method.
 *    - Add <search-menu>::getMenuItemTitle() method.
 *    - Add <search-menu>::isItemScheduledInAnyMenu() method.
 *    - Add <search-menu>::isItemScheduledInMenu() method.
 *    - Add <search-menu>::isItemScheduledInSection() method.
 *    - Track user interaction in <search-menu>.
 */

import {defineComponent, PropType} from "vue";
import TextInput from '../material-form/text-input.vue';
import {Menu, MenuSection, SearchMenuComponent, SearchMenuEmitViewMenuItemPayload} from "./defs";
import {TextInputComponent} from "../material-form/defs";
import {methodIsModelScheduled} from "../../helpers/menu-sections";

interface AutocompleteResult {
  [key: string]: any;

  menu: string;
  menu_id: number;
  mi_highlight: number;
  mi_item_group_name: string;
  mi_item_subgroup_name: string;
  mi_price: string;
  mi_id: number;
  mi_title: string;
  mi_subtitle: string;
  mi_description: string;
  ms_id: number;
  mt_title: string;
  section: string;
  tags: string[];
  _formatted: {
    [key: string]: any;

    menu: string;
    menu_id: number;
    mi_id: number;
    mi_item_group_name: string;
    mi_item_subgroup_name: string;
    mi_highlight: number;
    mi_price: string;
    mi_title: string;
    mi_subtitle: string;
    mi_description: string;
    ms_id: number;
    mt_title: string;
    section: string;
    tags: string[];
  };
}

interface AutocompleteResponse {
  exhaustiveNbHits: boolean;
  hits?: AutocompleteResult[];
  limit: number;
  nbHits: number;
  offset: number;
  processingTimeMs: number;
  query: string;
}

export default defineComponent({
  name: "search-menu",
  emits: {
    'view-menu-item': (payload: SearchMenuEmitViewMenuItemPayload) => true
  },
  components: {
    TextInput,
  },
  props: {
    searchIndex: {
      type: String,
      default: ''
    },
    searchKey: {
      type: String,
      default: ''
    },
    searchHost: {
      type: String,
      default: ''
    },
    menu: {
      type: Object as PropType<Menu | null>,
      default: null
    },
    relatedMenus: {
      type: Array as PropType<Menu[]>,
      default: () => []
    },
    minimizeWidget: {
      type: Boolean,
      default: false
    },
    isPublicDevice: {
      type: Boolean,
      default: false
    },
    isCartEnabled: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      searchTerm: '',
      autocompleteResults: [] as AutocompleteResult[],
      showResults: false,
      hideWidget: this.minimizeWidget,
      observer: null as IntersectionObserver | null,
      isStickyStuck: false,
      isFixedPosition: false,
      windowScrollY: 0,
      windowHeight: window.innerHeight,
      autocompleteMaxHeightRender: 0,

      abortController: null as AbortController | null,
      abortSignal: null as AbortSignal | null,
    };
  },
  mounted() {
    this.isFixedPosition = (window.getComputedStyle(this.$el).position === 'fixed');

    this.setupIntersectionObserver();

    document.addEventListener('click', () => {
      this.showResults = false;
      if (this.minimizeWidget) {
        this.hideWidget = true;
      }
    });

    window.addEventListener('scroll', () => {
      this.windowScrollY = window.scrollY;
    }, {passive: true});

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
  },
  watch: {
    autocompleteResults() {
      this.autocompleteMaxHeightRender++;
    },
    hideWidget() {
      if (this.hideWidget) {
        this.reset();
        window.pepTracking.event(this.$store.getters.displayTypeCapitalized + ' Menu', 'Close Search');
      } else {
        window.pepTracking.event(this.$store.getters.displayTypeCapitalized + ' Menu', 'Open Search');
      }
    }
  },
  computed: {
    autocompleteMaxHeight() {
      this.windowScrollY;
      this.autocompleteMaxHeightRender;
      let maxHeight = this.windowHeight;

      if (this.$refs.autocomplete) {
        maxHeight -= (this.$refs.autocomplete as HTMLLIElement).getBoundingClientRect().top;
      }

      if (this.$parent?.$el) {
        const sectionNavNode = this.$parent.$el.querySelector('.menu--section_nav');
        if (sectionNavNode) {
          maxHeight -= sectionNavNode.clientHeight;
        }
      }

      return maxHeight + 'px';
    },

    containerClasses() {
      return {
        'search--container-minimize': this.minimizeWidget,
        'search--container-open': this.minimizeWidget && !this.hideWidget,
        'sticky-stuck': this.isStickyStuck || this.isFixedPosition
      };
    },

    allMenuIdsByTitle(): { [key: string]: number } {
      const output: { [key: string]: number } = {};
      if (this.menu) {
        output[this.menu.menu_type.mt_title] = this.menu.menu_id;
      }
      this.relatedMenus.forEach(relatedMenu => {
        output[relatedMenu.menu_type.mt_title] = relatedMenu.menu_id;
      });
      return output;
    },

    allMenuIconsByTitle(): { [key: string]: string } {
      const output: { [key: string]: string } = {};
      if (this.menu) {
        output[this.menu.menu_type.mt_title] = this.menu.menu_icon;
      }
      this.relatedMenus.forEach(relatedMenu => {
        output[relatedMenu.menu_type.mt_title] = relatedMenu.menu_icon;
      });
      return output;
    },

  },
  methods: {
    isModelScheduled: methodIsModelScheduled,

    getResultTitle(result: AutocompleteResult): string {
      let output = this.getResultData(result, 'mi_title');
      const sectionTitle = this.getResultData(result, 'section');
      if (sectionTitle.length) {
        output += ' <span class="autocomplete_results--menu_title_section">in ' + sectionTitle + '</span>';
      }
      return output;
    },

    getResultData<K extends keyof AutocompleteResult>(result: AutocompleteResult, key: K): string {
      if (result._formatted[key].length) {
        if ('tags' === key) {
          return result._formatted.tags.join(', ');
        }
        return result._formatted[key];
      } else if (result[key].length) {
        if ('tags' === key) {
          return result.tags.join(', ');
        }
        return result[key];
      }
      return '';
    },
    getMenuItemTitle(result: AutocompleteResult): string {
      const titleParts = [];
      const menuTitle = this.getResultData(result, 'menu');
      if (menuTitle.length) {
        titleParts.push(menuTitle);
      }
      if (this.allMenuIconsByTitle[result.menu]) {
        titleParts.push('<i class="material-icons">' + this.allMenuIconsByTitle[result.menu] + '</i>');
      }
      const menuItemTitle = this.getResultData(result, 'mi_title');
      if (menuItemTitle.length) {
        titleParts.push(menuItemTitle);
      }
      return '<span>' + titleParts.join('</span><span>') + '</span>';
    },

    reset(force: boolean = false) {
      if (this.isPublicDevice || force) {
        if (this.$refs?.input) {
          (this.$refs.input as TextInputComponent).currentValue = '';
        }
        this.autocompleteResults = [];
      }
      this.showResults = false;
    },

    checkStickyStuck(entries: IntersectionObserverEntry[]) {
      this.isStickyStuck = (entries[0].intersectionRatio < 1);
    },

    setupIntersectionObserver() {
      if (window?.IntersectionObserver && !this.isFixedPosition) {
        //only need to set up the observer once
        if (!this.observer) {
          this.observer = new IntersectionObserver(this.checkStickyStuck, {
            threshold: [1]
          });
          if (this.$el.previousElementSibling) {
            this.observer.observe(this.$el.previousElementSibling);
          } else {
            this.observer.observe(this.$el);
          }
        }
      }
    },

    focusHandler() {
      if (this.searchTerm !== '') {
        this.showResults = true;
      }
    },

    submitAutocomplete() {
      if (this.searchTerm === '') {
        this.showResults = false;
      } else {

        if (this.abortSignal && this.abortController) {
          this.abortController.abort();
        }

        if (window?.AbortController) {
          this.abortController = new AbortController();
          this.abortSignal = this.abortController.signal;
        }

        fetch(this.searchHost + '/indexes/' + this.searchIndex + '/search', {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
            'X-Meili-API-Key': this.searchKey
          },
          redirect: 'follow',
          body: JSON.stringify({
            q: this.searchTerm,
            filter: 'menu_id=' + Object.values(this.allMenuIdsByTitle).join(' OR menu_id='),
            cropLength: 50,
            attributesToCrop: ['mi_title', 'mi_subtitle', 'mi_description'],
            attributesToHighlight: ['mi_title', 'mi_subtitle', 'mi_description', 'section'],
          }),
          signal: this.abortSignal
        })
          .then(response => {
            return response.json();
          })
          .then((json: AutocompleteResponse) => {
            if (json?.hits) {
              this.autocompleteResults = json.hits.filter((result: AutocompleteResult) => this.isItemScheduledInAnyMenu(result.mi_id));
              this.showResults = true;
            } else {
              this.autocompleteResults = [];
              this.showResults = false;
            }
            window.pepTracking.event(this.$store.getters.displayTypeCapitalized+ ' Menu', 'Search Results', this.searchTerm);
          })
          .catch(e => {
            console.log(e);
          })
          .finally(() => {
            this.abortController = null;
            this.abortSignal = null;
          })
      }
    },

    submitSearch() {
      if (!this.searchTerm.length) {
        this.reset();
      } else {
        //TODO: filter down to selected results?
      }
    },

    handleSearchHandleClick() {
      this.hideWidget = !this.hideWidget;

      if (!this.hideWidget) {
        (this.$refs.input as TextInputComponent | null)?.$el.querySelector('input')?.focus();
      } else {
        this.showResults = false;
      }
    },

    handleResultClick(result: AutocompleteResult) {
      this.$emit('view-menu-item', {menu_id: result.menu_id, mi_id: result.mi_id});
      this.showResults = false;
      this.hideWidget = true;
      this.reset();
      window.pepTracking.event(this.$store.getters.displayTypeCapitalized + ' Menu', 'Search View Item', result.mi_title + ' (' + result.mi_id + ')');
    },

    isItemScheduledInAnyMenu(mi_id: number): boolean {
      if (this.menu && this.isItemScheduledInMenu(mi_id, this.menu)) {
        return true;
      }

      for (let i = 0, l = this.relatedMenus.length; i < l; i++) {
        if (this.isItemScheduledInMenu(mi_id, this.relatedMenus[i])) {
          return true;
        }
      }

      return false;
    },

    isItemScheduledInMenu(mi_id: number, menu: Menu): boolean {
      for (let i = 0, l = menu.sections.length; i < l; i++) {
        if (this.isItemScheduledInSection(mi_id, menu.sections[i])) {
          return true;
        }
      }

      return false;
    },

    isItemScheduledInSection(mi_id: number, section: MenuSection): boolean {
      for (let groupTitle in section.grouped_items) {
        for (let subgroupTitle in section.grouped_items[groupTitle]) {
          for (let i = 0, l = section.grouped_items[groupTitle][subgroupTitle].length; i < l; i++) {
            if (section.grouped_items[groupTitle][subgroupTitle][i].mi_id === mi_id) {
              return (this as SearchMenuComponent).isModelScheduled(section.grouped_items[groupTitle][subgroupTitle][i].normalizedSchedules);
            }
          }
        }
      }

      for (let i = 0, l = section.children.length; i < l; i++) {
        if (this.isItemScheduledInSection(mi_id, section.children[i])) {
          return true;
        }
      }

      return false;
    },

  }
})
</script>

<style scoped>
.search--container {
  position: sticky;
  top: calc(3.2rem - 2px);
  z-index: 30;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  background-color: #ecefed;
  font-size: 1rem;
}

.menu_switcher--container-collapsable ~ .search--container:not(.search--container-open) {
  z-index: 10;
}

.search--container-minimize {
  position: fixed;
  top: 0;
  right: 0;
  transform: translateY(-100%);
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1), transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.search--container-minimize.search--container-open {
  transform: none;
}

.search--container .viewport {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  background-color: #ecefed;
  z-index: 5;
}

.search--container.search--container-open .viewport {
  padding-right: 6em;
}

.search--container .search--handle {
  position: absolute;
  top: 100%;
  right: 0;
  font-size: 3em;
  text-decoration: none;
  border-radius: 0.25em;
  /*box-shadow: 0 0 1em 0.5em rgb(236 239 237), inset 0 0 1em 0.5em rgb(236 239 237);*/
  z-index: 7;
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.search--container-open .search--handle {
  transform: translateY(-1.8em);
  padding-left: 0.5em;
  padding-right: 0.5em;
  font-size: 2.35em;
}

.search--handle::before {
  content: 'search';
}

.search--container-open .search--handle::before {
  content: 'cancel';
}

.autocomplete_results--container {
  list-style: none;
  font-size: 0.8rem;
  overflow: hidden;
  overflow-y: auto;
  padding: 0;
  position: absolute;
  background-color: #ecefed;
  width: 100%;
  left: 0;
}

.autocomplete_results--container-hide {
  opacity: 0;
  visibility: hidden;
  max-height: 0;
  overflow: hidden;
}

.autocomplete_results--container h5,
.autocomplete_results--container {
  margin-bottom: 0;
}

.autocomplete_results--container em {
  font-weight: bold;
  color: #007966;
}

.autocomplete_results--content {
  grid-row: 1 / -1;
  grid-column: 1;
  display: grid;
  grid-template-columns: 8em 1fr 44px;
  grid-gap: 0.75em;
  padding: 0.3875em 0.75em;
  cursor: pointer;
}

.autocomplete_results--container li:nth-child(odd) {
  background: #FFF;
}

.autocomplete_results--content:deep(em) {
  font-weight: bold;
  color: #007966;
  font-size: 1.2em;
}

.autocomplete_results--menu_icon {
  text-align: center;
  align-content: center;
  color: rgba(0, 0, 0, 0.67);
}

.autocomplete_results--menu_icon i {
  display: block;
  margin-bottom: 0.25em;
}

.autocomplete_results--menu_title:deep(.autocomplete_results--menu_title_section) {
  font-weight: normal;
  font-size: 0.8em;
  color: rgba(0, 0, 0, .54);
}

@media (prefers-contrast: more) {
  .autocomplete_results--menu_title:deep(.autocomplete_results--menu_title_section) {
    font-weight: bold;
    font-size: 1em;
    color: #000;
  }
}


.body-container.search-bar {
  padding-bottom: 0;
  padding-top: 0;

  /*disabled for now*/
  display: none;
}

.search--container.sticky-stuck,
.search--container.sticky-stuck .autocomplete_results--container {
  box-shadow: 0 -2px 6px 4px rgba(0, 0, 0, 0.54);
}

.search--container.sticky-stuck.search--container-minimize {
  box-shadow: none;
}

.search--container.sticky-stuck.search--container-open {
  box-shadow: inherit;
}


.item_button--container {
  position: relative;
  color: rgba(0, 0, 0, 0.54);
  width: 24px;
  justify-self: end;
  text-decoration: none;
}

.item_button--container-active {
  color: rgba(0, 103, 87, 0.54);
}

.item_button--container-view::before {
  content: 'visibility';
}


h4 {
  font-size: 1.1em;
  font-weight: bold;
  color: #000;
}


/*TABLET + MOBILE*/
@media screen and (max-width: 1024px) {
  .search--container .viewport {
    padding-right: 32px;
  }
}



.search--overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: -1;
  visibility: hidden;
  opacity: 0;
  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);
}

.search--overlay.search--overlay-active {
  visibility: visible;
  height: 120vh;
  opacity: 1;
}
</style>